import React, { useContext, useEffect, useState, useRef } from "react";
import FormDataContext from "../../../Contexts/FormData.context";
import { formActionEnum, jsonKeyMapping } from "../../../Config/Constants";
import { TemplateObject } from "../../../Models/TemplateObject";
import InputWrapper from "./InputWrapper";
import InputContext from "../../../Contexts/Input.Context";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";

interface Props {
  formElement: TemplateObject;
  handleDateChange: (id: string, type: string, date: any) => void;
}

const InputDate: React.FC<Props> = ({ formElement, handleDateChange }) => {
  const { state, dispatch } = useContext(FormDataContext);
  const { setConditionalInput } = useContext(InputContext);
  const { formData } = state;
  const datePickerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());

  useEffect(() => {
    if (formElement && formElement[jsonKeyMapping.FORM_DATE_FORMAT]) {
      const formElementData = {
        [`meta-data-${formElement[jsonKeyMapping.FORM_ID]}`]: {
          timeFormat: formElement[jsonKeyMapping.FORM_DATE_FORMAT],
        },
      };

      dispatch({
        type: formActionEnum.SET_FORM_DATA,
        payload: formElementData,
      });
    }
  }, [formElement]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const wrapper = datePickerRef.current;
      if (!wrapper) return;
      const popper = document.querySelector('.react-datepicker-popper');
      // If click is inside wrapper or active popper, ignore
      if (wrapper.contains(target) || (popper && popper.contains(target))) {
        return;
      }
      setOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const loadDateFields = (date: Date) => {
    if (
      date &&
      formElement[jsonKeyMapping.FORM_INPUT_RADIO_MORE_FIELDS] !== undefined
    ) {
      setConditionalInput({
        formElement: formElement[jsonKeyMapping.FORM_INPUT_RADIO_MORE_FIELDS],
        parentLabel: formElement[jsonKeyMapping.FORM_LABEL],
        value: formElement[jsonKeyMapping.FORM_VALUE],
      });
    } else {
      setConditionalInput(null);
    }

    const formattedDate = date ? moment(date).format("MM-DD-YYYY") : null;
    setOpen(false);
    handleDateChange(
      formElement[jsonKeyMapping.FORM_ID],
      "date",
      formattedDate
    );
  };

  // Derive placeholder from JSON (support several common keys) with fallback
  const jsonPlaceholder =
    formElement.placeholder ||
    (formElement as any).placeHolder ||
    formElement[jsonKeyMapping.FORM_PLACEHOLDER] ||
    (formElement as any)["placeholderText"] ||
    "Select a date";

  // Block manual typing in the input field while preserving focus and picker toggle
  const handleRawChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    e.preventDefault();
    // Reset displayed value to stored formData to avoid stray characters
    const currentValue = formData[formElement[jsonKeyMapping.FORM_ID]] || "";
    if (e.target.value !== currentValue) {
      e.target.value = currentValue;
    }
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    // Allow navigation keys & tab for accessibility; block character input
    const allowedKeys = [
      "Tab",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Escape",
      "Enter",
      "Home",
      "End",
    ];
    if (allowedKeys.includes(e.key)) return;
    // Space toggles open state; keep that
    if (e.key === " " || e.key === "Spacebar") {
      setOpen((prev) => !prev);
      e.preventDefault();
      return;
    }
    // Prevent all other key presses (numbers, letters, etc.)
    e.preventDefault();
  };

  const isInsideMissingInfoModal = () => {
    return !!datePickerRef.current?.closest('.missing-info-modal');
  };

  return (
    <InputWrapper
      formElement={formElement}
      label={formElement[jsonKeyMapping.FORM_LABEL] || ""}
      labelsub={formElement[jsonKeyMapping.FORM_LABEL_SUB] || ""}
      subtext={formElement[jsonKeyMapping.FORM_SUBTEXT] || ""}
    >
      <div style={{ position: "relative" }} ref={datePickerRef}>
        <DatePicker
          // selected={selectedDate}
          onChange={loadDateFields}
          open={open}
          dateFormat={formElement[jsonKeyMapping.FORM_DATE_FORMAT]}
          minDate={formElement.min ? new Date(formElement.min) : null}
          maxDate={formElement.max ? new Date(formElement.max) : null}
          className={`form-control ${
            formElement.inputClass ? formElement.inputClass : ""
          }`}
          id={formElement[jsonKeyMapping.FORM_ID]}
          value={formData[formElement[jsonKeyMapping.FORM_ID]]}
          onInputClick={() => setOpen((prev) => !prev)}
          placeholderText={jsonPlaceholder}
          peekNextMonth={true}
          showMonthDropdown={true}
          showYearDropdown={true}
          dropdownMode="select"
          showIcon
          /* Always portal so calendar is not constrained by modal scroll; rely on popper modifiers for placement */
          portalId={'datepickers-portal'}
          popperPlacement="bottom-start"
          popperClassName={isInsideMissingInfoModal() ? 'datepicker-popper-modal' : 'datepicker-popper-portal'}
          popperModifiers={[{ name: 'preventOverflow', options: { rootBoundary: 'viewport', tether: false } }]}
          icon={
            <svg
              width="14"
              height="16"
              viewBox="0 0 14 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4.75 0.75V2H9.25V0.75C9.25 0.34375 9.5625 0 10 0C10.4062 0 10.75 0.34375 10.75 0.75V2H12C13.0938 2 14 2.90625 14 4V4.5V6V14C14 15.125 13.0938 16 12 16H2C0.875 16 0 15.125 0 14V6V4.5V4C0 2.90625 0.875 2 2 2H3.25V0.75C3.25 0.34375 3.5625 0 4 0C4.40625 0 4.75 0.34375 4.75 0.75ZM1.5 6V14C1.5 14.2812 1.71875 14.5 2 14.5H12C12.25 14.5 12.5 14.2812 12.5 14V6H1.5Z"
                fill="#16238d"
              />
            </svg>
          }
          // Make input non-typable
          readOnly
          onChangeRaw={handleRawChange}
          onKeyDown={handleKeyDown}
        />
      </div>
    </InputWrapper>
  );
};

export default InputDate;
